import { Link, NavLink } from 'react-router-dom'
import { useAuth } from '../lib/auth'
import { canWrite } from '../lib/types'
import { Button, cx } from './ui'
import { ThemeToggle } from './ThemeToggle'

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)

export function TopBar({
  onOpenSearch,
  onToggleNav,
}: {
  onOpenSearch: () => void
  onToggleNav: () => void
}) {
  const { user, email, name, role, loading, admin, staff, signIn, signOut } = useAuth()

  return (
    <header className="sticky top-0 z-40 border-b border-line bg-surface/85 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-[1600px] items-center gap-3 px-4 sm:px-6">
        <button
          onClick={onToggleNav}
          aria-label="Toggle navigation"
          className="-ml-1 rounded-[var(--radius-sh)] p-2 text-muted hover:bg-sunken lg:hidden"
        >
          <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        <Link to="/" className="flex shrink-0 items-center gap-2.5" aria-label="SightHub Wiki — home">
          <img src="/sighthub-logo.png" alt="" width={28} height={28} className="size-7 object-contain" />
          <span className="text-[15px] font-semibold tracking-tight text-ink">
            SightHub <span className="text-subtle">Wiki</span>
          </span>
        </Link>

        <button
          onClick={onOpenSearch}
          className="ml-2 hidden h-9 w-72 items-center gap-2 rounded-[var(--radius-sh)] border border-line bg-sunken px-3 text-sm text-subtle transition-colors hover:border-line-strong sm:flex"
        >
          <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="7" />
            <path strokeLinecap="round" d="M20 20l-3.5-3.5" />
          </svg>
          <span className="flex-1 text-left">Search…</span>
          <kbd className="rounded border border-line bg-surface px-1.5 font-mono text-[10px]">
            {isMac ? '⌘' : 'Ctrl'} K
          </kbd>
        </button>

        <div className="flex-1" />

        <nav className="hidden items-center gap-1 md:flex">
          <TopLink to="/search">Search</TopLink>
          {user && <TopLink to="/favorites">Favorites</TopLink>}
          {staff && <TopLink to="/export">Export</TopLink>}
          {canWrite(role) && <TopLink to="/admin">{admin ? 'Admin' : 'Authoring'}</TopLink>}
          {canWrite(role) && (
            <Link
              to="/new"
              className="ml-1 rounded-[var(--radius-sh)] border border-line bg-surface px-2.5 py-1.5 text-sm font-medium text-ink shadow-card hover:bg-sunken"
            >
              New doc
            </Link>
          )}
        </nav>

        <ThemeToggle />

        {loading ? (
          <div className="h-8 w-24 animate-pulse rounded-[var(--radius-sh)] bg-sunken" />
        ) : user ? (
          <div className="flex items-center gap-2">
            <span className="hidden text-right leading-tight sm:block">
              <span
                title={email ?? undefined}
                className="block max-w-[180px] truncate text-xs font-medium text-ink"
              >
                {name ?? email ?? user.email}
              </span>
              <span className="block font-mono text-[10px] text-subtle">{role ?? 'unknown role'}</span>
            </span>
            <Button size="sm" variant="ghost" onClick={() => void signOut()}>
              Sign out
            </Button>
          </div>
        ) : (
          <Button size="sm" variant="primary" onClick={() => void signIn()}>
            Sign in
          </Button>
        )}
      </div>
    </header>
  )
}

const TopLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
  <NavLink
    to={to}
    className={({ isActive }) =>
      cx(
        'rounded-[var(--radius-sh)] px-2.5 py-1.5 text-sm transition-colors',
        isActive ? 'text-ink' : 'text-muted hover:text-ink',
      )
    }
  >
    {children}
  </NavLink>
)
